import { h } from './dom.js';
import { installArtworkCoverRecovery } from './artwork-image.js';
import { systemTag } from '../system-label.js';
import { releaseBadgeNode, releaseBadgeSuffixNode } from './release-badge.js';

/**
 * One tile in a game grid — shared between the library index and the
 * homepage shelves.
 *
 * Pure-props: this component does NOT import any API module. All
 * server-side concerns (resolving a cover URL, toggling a favorite,
 * adding to a list) are callbacks injected by the caller. This keeps
 * the library bundle and the main-app bundle free of each other's
 * dependencies.
 *
 * Props:
 *   rom: {
 *     name, displayName, system, hash, size, type, variant,
 *     game_id?, game_slug?, genres?, release_year?, developer?,
 *     rating?, favorite?,
 *   }
 *   coverUrl        — string. Caller resolves artwork; pass `null`/undefined
 *                     for the placeholder-only path.
 *   placeholderUrl  — string, defaults to '/library/public/placeholder-boxart.svg'.
 *                     Homepage may pass its own placeholder path.
 *   detailHref      — canonical game-detail URL. When present, the title is a
 *                     real link whose stretched hit area covers the card.
 *   onPlay(rom)     — launch the game.
 *   onDetail?(rom, tile) — optional SPA/panel navigation callback for the
 *                     detail link. Modified clicks keep native link behavior.
 *   onFavorite?(rom, next) → Promise  — async toggle. Resolves to new
 *                     favorite state (true/false) on success; throws with
 *                     `{status}` on failure so the tile can roll back
 *                     optimistic UI. If omitted, no favorite button.
 *   onAddToList?({ gameId, title })   — called when the "+" action is
 *                     pressed. If omitted, no Add-to-list button.
 *   queued?         — boolean. Initial "is in My Queue" state for the
 *                     queue toggle button.
 *   onQueueToggle?(rom, next) → Promise — async one-click queue toggle.
 *                     Throws with `{status}` on failure so the tile can
 *                     roll back optimistic UI. If omitted, no queue button.
 *   owned           — boolean, defaults false. Renders a "MY ROM" chip —
 *                     this is the viewer's own ROM/claim, not just a game
 *                     that's in the bundled catalog.
 *   onRemove?(rom)  — renders a ✕ hover action that removes the caller's
 *                     own row. The caller decides when it applies (e.g.
 *                     only synthetic rows, or only while a "My ROMs"
 *                     facet is active) — omit to hide the action entirely.
 */
export function GridTile({
  rom,
  coverUrl,
  placeholderUrl = '/library/public/placeholder-boxart.svg',
  detailHref,
  onPlay,
  onDetail,
  onFavorite,
  onAddToList,
  onQueueToggle,
  queued,
  onReport,
  locked = false,
  owned = false,
  onRemove,
}) {
  const coverSrc = coverUrl || placeholderUrl;

  // ── Cover image ────────────────────────────────────────────────────
  const img = h('img', {
    class: 'lib-tile-cover-img',
    src: coverSrc,
    alt: '',
    loading: 'lazy',
    decoding: 'async',
    draggable: 'false',
  });
  if (coverUrl) installArtworkCoverRecovery(img, () => {
    img.src = placeholderUrl;
    img.closest('.lib-tile-cover')?.classList.add('lib-tile-no-art');
  });
  img.addEventListener('load', () => {
    if (!img.src.endsWith(placeholderUrl)) {
      img.closest('.lib-tile-cover')?.classList.add('lib-tile-has-art');
    }
  }, { once: true });

  // ── System chip (top-right of cover) ───────────────────────────────
  // Positioned against .lib-tile, not .lib-tile-cover (mirrors
  // .lib-tile-fav-btn below): the cover clips to its rounded corners and the
  // hover zoom, and the shared tag is wider than the cover on a narrow tile
  // now that it carries a space ("ATARI 2600") — anchored to the cover it
  // clipped off the leading letter. .lib-tile has overflow: visible and
  // room to spare (its own padding box) for the chip's full width.
  const systemChip = rom.system
    ? h('span', { class: 'lib-tile-system' }, systemTag(rom.system))
    : null;

  // ── Lock chip (top-left) — BYO-ROM mode, game not owned ────────────
  const lockChip = locked
    ? h('span', { class: 'lib-tile-lock', title: 'Upload your ROM to play', 'aria-label': 'Locked: upload your ROM to play' }, '🔒')
    : null;

  // ── Owned chip (top-left) — the viewer's own ROM/claim ─────────────
  // Mutually exclusive with the lock in practice: owned ⇒ playable.
  const ownedChip = owned
    ? h('span', { class: 'lib-tile-owned', title: 'In your ROM library', 'aria-label': 'In your ROM library' }, 'MY ROM')
    : null;

  // ── Unavailable chip — stale claim (bundled twin removed) ──────────
  // A claim stores no bytes of its own; when the catalog no longer has
  // its bundled target, launching would 404 — so no play action, just an
  // explicit state chip. The row stays removable.
  const unavailable = rom.user_rom === true && rom.unavailable === true;
  const unavailableChip = unavailable
    ? h('span', { class: 'lib-tile-unavailable', title: 'Unavailable: the bundled copy of this claim was removed', 'aria-label': 'Unavailable: the bundled copy of this claim was removed' }, 'UNAVAILABLE')
    : null;

  // ── Queue toggle (hover action bar) ────────────────────────────────
  // One-click add/remove for My Queue. Optimistic with revert-on-failure,
  // mirroring the favorite button below.
  let queueBtn = null;
  if (rom.game_id != null && onQueueToggle) {
    let inQueue = !!queued;
    const applyQueueState = () => {
      queueBtn.textContent = inQueue ? '✓' : '+Q';
      queueBtn.classList.toggle('active', inQueue);
      const title = inQueue
        ? `Remove ${rom.displayName} from your queue`
        : `Add ${rom.displayName} to your queue`;
      queueBtn.title = title;
      queueBtn.setAttribute('aria-label', title);
      queueBtn.setAttribute('aria-pressed', inQueue ? 'true' : 'false');
    };
    queueBtn = h('button', {
      type: 'button',
      class: 'lib-tile-action lib-tile-action-queue',
      onclick: async (e) => {
        e.stopPropagation();
        const next = !inQueue;
        inQueue = next;
        applyQueueState();
        try {
          await onQueueToggle(rom, next);
        } catch (err) {
          inQueue = !next;
          applyQueueState();
          if (err?.status === 401) {
            queueBtn.title = 'Sign in to use your queue';
          }
          console.warn('[tile] queue toggle failed:', err);
        }
      },
    }, '');
    applyQueueState();
  }

  // ── Hover action bar (bottom of cover, appears on hover/focus) ─────
  // When `locked` (BYO-ROM mode and the user doesn't own this game), the play
  // button becomes a 🔒 "upload your ROM" affordance. It still calls onPlay —
  // the launch funnels into the main app, which prompts a BYO upload on the
  // gated 403 (the main app owns the zip-aware extract + hash-match flow).
  const actionButtons = [
    (onPlay && !unavailable) ? h('button', {
      type: 'button',
      class: `lib-tile-action lib-tile-action-play${locked ? ' lib-tile-action-locked' : ''}`,
      title: locked ? `Upload your ROM to play ${rom.displayName}` : `Play ${rom.displayName}`,
      'aria-label': locked ? `Upload your ROM to play ${rom.displayName}` : `Play ${rom.displayName}`,
      onclick: (e) => { e.stopPropagation(); onPlay?.(rom); },
    }, locked ? '🔒' : '▶') : null,
    (rom.game_id != null && onAddToList) ? h('button', {
      type: 'button',
      class: 'lib-tile-action lib-tile-action-addlist',
      title: `Add ${rom.displayName} to a list`,
      'aria-label': `Add ${rom.displayName} to a list`,
      onclick: (e) => {
        e.stopPropagation();
        onAddToList({ gameId: Number(rom.game_id), title: rom.displayName });
      },
    }, '+') : null,
    queueBtn,
    onReport ? h('button', {
      type: 'button',
      class: 'lib-tile-action lib-tile-action-report',
      title: `Report a problem with ${rom.displayName}`,
      'aria-label': `Report a problem with ${rom.displayName}`,
      onclick: (e) => { e.stopPropagation(); onReport(rom); },
    }, '🚩') : null,
    onRemove ? h('button', {
      type: 'button',
      class: 'lib-tile-action lib-tile-action-remove',
      title: `Remove ${rom.displayName} from your ROMs`,
      'aria-label': `Remove ${rom.displayName} from your ROMs`,
      onclick: (e) => { e.stopPropagation(); onRemove(rom); },
    }, '✕') : null,
  ].filter(Boolean);
  const actionBar = actionButtons.length
    ? h('div', { class: 'lib-tile-actions' }, actionButtons)
    : null;

  // ── Metadata line: year · genre · developer ────────────────────────
  const metaBits = [];
  if (rom.release_year) metaBits.push(String(rom.release_year));
  if (rom.genres?.length) metaBits.push(rom.genres[0]);
  else if (rom.variant) metaBits.push(rom.variant);
  if (rom.developer && metaBits.length < 2) metaBits.push(rom.developer);

  // ── Rating pill (only if available) ────────────────────────────────
  const ratingEl = rom.rating != null
    ? h('span', {
        class: `lib-tile-rating ${rom.rating >= 7 ? 'good' : rom.rating >= 5 ? 'mid' : 'low'}`,
        title: `Rating: ${rom.rating.toFixed(1)} / 10`,
      }, rom.rating.toFixed(1))
    : null;

  // ── Favorite toggle (bottom-right) ─────────────────────────────────
  // Requires a rom.hash (favorites are keyed on system + hash) AND an
  // onFavorite callback (caller-side persistence). Absent either, no button.
  let favBtn = null;
  if (rom.hash && onFavorite) {
    const applyFavoriteState = () => {
      const favorite = !!rom.favorite;
      favBtn.classList.toggle('active', favorite);
      favBtn.setAttribute('aria-pressed', favorite ? 'true' : 'false');
      favBtn.title = favorite ? 'Unfavorite' : 'Add to favorites';
      favBtn.setAttribute(
        'aria-label',
        favorite ? `Unfavorite ${rom.displayName}` : `Favorite ${rom.displayName}`,
      );
      favBtn.textContent = favorite ? '★' : '☆';
    };
    favBtn = h('button', {
      type: 'button',
      class: 'lib-tile-fav-btn',
      onclick: async (e) => {
        e.stopPropagation();
        const next = !rom.favorite;
        // Optimistic update.
        rom.favorite = next;
        applyFavoriteState();
        try {
          await onFavorite(rom, next);
        } catch (err) {
          // Revert on failure. 401 = not signed in; anything else is
          // transient. Keep the UI coherent either way.
          rom.favorite = !next;
          applyFavoriteState();
          if (err?.status === 401) {
            favBtn.title = 'Sign in to favorite';
          }
          console.warn('[tile] favorite toggle failed:', err);
        }
      },
    });
    applyFavoriteState();
  }

  // ── Feature badges (achievements / score tracking) ──────────────────
  const hasAch = (rom.achievement_count || 0) > 0;
  const hasScore = !!rom.score_supported;
  const badgesEl = (hasAch || hasScore)
    ? h('div', { class: 'lib-tile-badges' },
        hasAch ? h('span', {
          class: 'lib-tile-badge',
          role: 'img',
          title: `${rom.achievement_count} achievements`,
          'aria-label': `${rom.achievement_count} achievements`,
        }, h('span', {
          class: 'rp-badge-icon rp-badge-icon--achievements',
          'data-label': 'Achievements',
          'aria-hidden': 'true',
        })) : null,
        hasScore ? h('span', {
          class: 'lib-tile-badge',
          role: 'img',
          title: 'High score tracking',
          'aria-label': 'High score tracking',
        }, h('span', {
          class: 'rp-badge-icon rp-badge-icon--score',
          'data-label': 'High score',
          'aria-hidden': 'true',
        })) : null,
      )
    : null;

  // ── Assemble ───────────────────────────────────────────────────────
  const detailLink = detailHref
    ? h('a', {
        class: 'lib-tile-detail-link',
        href: detailHref,
      }, rom.displayName, releaseBadgeSuffixNode(rom))
    : null;
  const releaseBadge = releaseBadgeNode(rom);
  const info = h('div', { class: 'lib-tile-info' },
    h('div', { class: 'lib-tile-title-row' },
      h('h3', { class: 'lib-tile-title', title: rom.displayName },
        detailLink || rom.displayName,
        !detailLink ? releaseBadgeSuffixNode(rom) : null,
      ),
      releaseBadge,
    ),
    metaBits.length
      ? h('p', { class: 'lib-tile-meta', title: metaBits.join(' · ') }, metaBits.join(' · '))
      : h('p', { class: 'lib-tile-meta lib-tile-meta-faded' }, humanSize(rom.size)),
    ratingEl,
  );
  const cover = h('div', { class: 'lib-tile-cover' },
    img,
    lockChip,
    ownedChip,
    unavailableChip,
    badgesEl,
    actionBar,
  );
  const tile = h('article', {
    class: `lib-tile${locked ? ' lib-tile-locked' : ''}`,
  },
    // Keep the detail link first in DOM/tab order. CSS orders the cover
    // visually above this information block.
    info,
    cover,
    // Sibling of cover, not nested in it — see the systemChip comment above.
    systemChip,
    favBtn,
  );

  // Preserve native link behavior for new-tab/window and modified clicks.
  // Plain activation can stay inside the library SPA/detail panel.
  if (detailLink && onDetail) {
    detailLink.addEventListener('click', (e) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      e.preventDefault();
      onDetail(rom, tile);
    });
  }

  return tile;
}

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
